/**
 * Represents a page of data with a token to retrieve the next page.
 */
export interface CursorPaged<T> {
  next: string | null
  total?: number
  data: T[]
}

export const CursorPaged = {
  /**
   * Creates a {@link CursorPaged} from the given items.
   *
   * @param items
   * @param next
   */
  of: <T>(items: T[], next: string | null = null): CursorPaged<T> => ({
    data: items,
    next,
  }),

  /**
   * Returns an empty page.
   */
  empty: <T>(): CursorPaged<T> => ({
    data: [],
    next: null,
  }),

  /**
   * Maps the inner items in the page one by one.
   *
   * @param paged
   * @param project
   */
  map: <T, R>(
    paged: CursorPaged<T>,
    project: (items: T[]) => R[],
  ): CursorPaged<R> => ({
    data: project(paged.data),
    total: paged.total,
    next: paged.next,
  }),

  /**
   * Maps the inner items in the page one by one.
   *
   * @param paged
   * @param project
   */
  mapT: <T, R>(
    paged: CursorPaged<T>,
    project: (item: T) => R,
  ): CursorPaged<R> => ({
    data: paged.data.map(project),
    total: paged.total,
    next: paged.next,
  }),
}

/**
 * Paging-related query parameters.
 */
export interface CursorPageQuery {
  /**
   * Max amount of records to return.
   */
  limit: number

  /**
   * Cursor for the next page, if any.
   */
  after: string | null | undefined
}
